import React, { useEffect, useMemo, useState } from 'react';

type LoadingStep = {
  id: string;
  label: string;
  duration?: number | null;
};

const pickRandom = (values: number[]) =>
  values[Math.floor(Math.random() * values.length)];

const LOADING_STEPS: LoadingStep[] = [
  {
    id: "analyzing",
    label: "Analyzing your queries",
    duration: pickRandom([3, 4]),
  },
  {
    id: "permissions",
    label: "Figuring data sources",
    duration: pickRandom([1, 2, 3]),
  },
  {
    id: "retrieving",
    label: "Retrieving data",
    duration: pickRandom([4, 5]),
  },
  {
    id: "processing",
    label: "Processing data",
    duration: pickRandom([6, 7, 8]),
  },
  {
    id: "preparing",
    label: "Preparing your answer",
    duration: null,
  },
];

const LoadingProgress: React.FC<{ startTime?: number }> = ({ startTime }) => {
  // Use global constant steps so durations are consistent across re-mounts/chats
  const steps = LOADING_STEPS;

  // If no startTime provided, use current time (calculated once on mount)
  const effectiveStartTime = useMemo(() => startTime ?? Date.now(), [startTime]);

  // Track current time to trigger re-renders
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // Update every 100ms for smooth progress
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 100);
    return () => window.clearInterval(intervalId);
  }, []);

  // Derived state calculation
  const elapsedSeconds = Math.max(0, (now - effectiveStartTime) / 1000);

  let currentStepIndex = 0;
  let accumulatedTime = 0;
  let currentStep = steps[0];

  // Find which step we are in
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    currentStep = step;
    currentStepIndex = i;

    // If undefined duration (preparing step), we stay here forever
    if (step.duration === null || step.duration === undefined) {
      break;
    }

    // If we are within this step's window
    if (elapsedSeconds < accumulatedTime + step.duration) {
      break;
    }

    accumulatedTime += step.duration;
  }

  // Calculate progress
  // Total progress is (Completed Steps) + (Progress in Current Step)
  // We map this to 0-100%
  const totalSteps = steps.length - 1; // Exclude the final "end state" or consider last one as destination?
  // Previous logic: (currentIndex / (steps.length - 1)) * 100 + partial

  let progressInCurrentStep = 0;
  if (currentStep.duration) {
    const timeInStep = elapsedSeconds - accumulatedTime;
    progressInCurrentStep = Math.min(1, Math.max(0, timeInStep / currentStep.duration));
  } else {
    // If duration is null (Preparing...), we are effectively at 100% of previous steps 
    // and maybe waiting? The previous logic mapped preparing to 100%.
    progressInCurrentStep = 0;
  }

  // If we are at the very last step (preparing), progress should be 100%?
  // The original implementation mapped the LAST step (index 4) to 100%.
  // steps.length = 5. denominator = 4.
  // If index = 4, 4/4 = 100%.

  const baseProgress = (currentStepIndex / (steps.length - 1)) * 100;
  const stepContribution = (100 / (steps.length - 1)) * progressInCurrentStep;

  const progress = Math.min(100, baseProgress + stepContribution);

  return (
    <div className="w-[320px] rounded-xl border border-gray-200 bg-white p-5 text-sm text-slate-600 shadow-sm">
      <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200">
          <div className="h-5 w-5 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
        </div>
        <div className="space-y-1">
          <p className="text-base font-semibold text-slate-900">
            {currentStep?.label}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoadingProgress;
